import axios from 'axios';
import { getAuthHeader } from './authService';

export interface Team {
  id: number;
  departmentId: number;
  departmentName?: string;
  name: string;
  logo?: string;
  location?: string;
  email?: string;
  phone?: string;
  universityId: number;
  createdBy: number;
  createdByType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  membershipStatus?: string;
  memberCount?: number;
  maxMembers?: number;
  organizationName?: string;
  description?: string;
  isPublic?: boolean;
  department?: string;
}

export interface TeamMembershipRequest {
  teamId: number;
  studentId?: number;
  action?: string;
  message?: string;
}

class TeamService {
  private readonly API_URL = process.env.REACT_APP_API_BASE_URL || '/api';

  async getMyTeams(): Promise<Team[]> {
    try {
      // Check user type from stored auth data
      const userData = localStorage.getItem('user');
      const userType = userData ? JSON.parse(userData).userType : null;
      
      let endpoint = `${this.API_URL}/teams`;
      
      // Use different endpoints based on user type
      if (userType === 'student') {
        endpoint = `${this.API_URL}/student-teams/joined`;
      } else if (userType === 'provider') {
        // Providers should use the regular teams endpoint, not student-teams
        endpoint = `${this.API_URL}/teams`;
      }
      
      const response = await axios.get(endpoint, { headers: getAuthHeader() });
      return response.data;
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
  }

  async getTeamById(id: number): Promise<Team> {
    try {
      const response = await axios.get(
        `${this.API_URL}/teams/${id}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching team:', error);
      throw error;
    }
  }

  async activateTeam(id: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/teams/${id}/activate`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error activating team:', error);
      throw error;
    }
  }

  async deactivateTeam(id: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/teams/${id}/deactivate`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error deactivating team:', error);
      throw error;
    }
  }

  async requestTeamAccess(request: TeamMembershipRequest): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/teams/request-access`,
        request,
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error requesting team access:', error);
      throw error;
    }
  }

  async getStudentTeams(studentId: number): Promise<Team[]> {
    try {
      const response = await axios.get(
        `${this.API_URL}/teams/student/${studentId}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching student teams:', error);
      throw error;
    }
  }

  // Student-specific endpoints
  async getAvailableTeams(): Promise<Team[]> {
    try {
      const response = await axios.get(
        `${this.API_URL}/student-teams/available`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching available teams:', error);
      throw error;
    }
  }

  async getJoinedTeams(): Promise<Team[]> {
    try {
      const response = await axios.get(
        `${this.API_URL}/student-teams/joined`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching joined teams:', error);
      throw error;
    }
  }

  async getPendingRequests(): Promise<Team[]> {
    try {
      const response = await axios.get(
        `${this.API_URL}/student-teams/pending`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      throw error;
    }
  }

  async joinTeam(teamId: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/student-teams/${teamId}/join`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error joining team:', error);
      throw error;
    }
  }

  async leaveTeam(teamId: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/student-teams/${teamId}/leave`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error leaving team:', error);
      throw error;
    }
  }

  async cancelJoinRequest(teamId: number): Promise<void> {
    try {
      await axios.post(
        `${this.API_URL}/student-teams/${teamId}/cancel-request`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      console.error('Error cancelling join request:', error);
      throw error;
    }
  }

  async searchTeams(query?: string, departmentId?: number): Promise<Team[]> {
    try {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (departmentId) params.append('departmentId', departmentId.toString());
      
      const response = await axios.get(
        `${this.API_URL}/student-teams/search${params.toString() ? '?' + params.toString() : ''}`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error('Error searching teams:', error);
      throw error;
    }
  }
}

const teamService = new TeamService();
export default teamService;